import React from "react";
import SchemaFieldRenderer from "@/components/post-ad/SchemaFieldRenderer";

/**
 * Renders the schema-declared per-category attributes ("attribute_fields")
 * for the current form category. `top_level_fields` (make/model/year/
 * shipping_options — vehicles only) render as top-level form keys; every
 * other field value round-trips through `form.attributes[key]`.
 *
 * ITER-246 (car parity v2): when a field carries a `group` label
 * (e.g. "Background check" / "Energy Economy Rating") we render each
 * group as its own labelled sub-section, matching the legacy site's
 * "Select Rating / Average / Description" trios. Ungrouped fields fall
 * into the default "Basic" grid.
 *
 * ITER-253 (auto-filled dot): every field that was populated by the
 * plate-lookup widget gets a small green "PrefilledDot" next to its
 * label. Editing the field clears the dot for that key via the
 * `clearPrefill([keys])` callback.
 */
export default function SchemaAttributesSection({
  schema,
  form,
  set,
  prefilledKeys = {},
  clearPrefill = () => {},
}) {
  if (!schema) return null;
  const topLevel = schema.top_level_fields || [];
  const attrs = schema.attribute_fields || [];
  if (!topLevel.length && !attrs.length) return null;

  const setAttr = (key, value) => {
    set({ attributes: { ...(form.attributes || {}), [key]: value } });
    clearPrefill([`attributes.${key}`]);
  };
  const setTop = (key, value) => {
    set({ [key]: value });
    clearPrefill([key]);
  };

  // Preserve schema order while bucketing fields by `group`. The
  // untitled bucket ("") is always rendered first.
  const groups = [];
  const groupIndex = new Map();
  attrs.forEach((f) => {
    const g = f.group || "";
    if (!groupIndex.has(g)) {
      groupIndex.set(g, groups.length);
      groups.push({ title: g, fields: [] });
    }
    groups[groupIndex.get(g)].fields.push(f);
  });

  const wideCols = (f) =>
    f.input === "slots" ||
    f.input === "multi" ||
    f.input === "richtext" ||
    f.key === "Amenities" ||
    f.key === "Features";

  return (
    <div
      className="rounded-2xl border border-[color:var(--brand-blue-wash)] bg-[color:var(--brand-blue-wash)]/40 p-5 md:p-6 space-y-5"
      data-testid="schema-attributes-section"
    >
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.14em] text-[color:var(--brand-blue)]">
          Category details
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          Fill what applies — helps buyers find your listing.
        </div>
      </div>

      {!!topLevel.length && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="schema-top-level-grid">
          {topLevel.map((f) => (
            <div
              key={f.key}
              className={f.input === "shipping" ? "md:col-span-3" : ""}
            >
              <SchemaFieldRenderer
                field={f}
                value={form[f.key]}
                onChange={(v) => setTop(f.key, v)}
                categorySlug={schema?.category?.slug || form.category_slug}
                source="top"
                prefilled={!!prefilledKeys[f.key]}
              />
            </div>
          ))}
        </div>
      )}

      {groups.map((grp, gi) => (
        <div
          key={grp.title || `g-${gi}`}
          data-testid={
            grp.title
              ? `schema-attr-group-${grp.title
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")
                  .replace(/^-|-$/g, "")}`
              : "schema-attr-group-basic"
          }
        >
          {grp.title && (
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600 mb-2">
              {grp.title}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {grp.fields.map((f) => (
              <div key={f.key} className={wideCols(f) ? "md:col-span-3" : ""}>
                <SchemaFieldRenderer
                  field={f}
                  value={(form.attributes || {})[f.key]}
                  onChange={(v) => setAttr(f.key, v)}
                  categorySlug={schema?.category?.slug || form.category_slug}
                  source="attr"
                  prefilled={!!prefilledKeys[`attributes.${f.key}`]}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
